import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Coins, ShieldCheck, BookOpen, MessageCircle, Gift, FileText, Menu, Award, LifeBuoy, Bot, Crown, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { useAuth, isStaff } from "@/context/AuthContext";
import { GRADES, ROLE_NAMES, coinLabel, PLAN_NAMES } from "@/lib/constants";
import { toast } from "sonner";
import { errText } from "@/lib/api";

const NAV = [
  { to: "/", label: "Предметы", id: "subjects" },
  { to: "/chat", label: "Сообщество", id: "chat" },
  { to: "/ai", label: "AI-помощник", id: "ai" },
  { to: "/rewards", label: "Награды", id: "rewards" },
  { to: "/my", label: "Мои файлы", id: "my-files" },
];

const moreLinks = (user) => [
  { to: "/my", label: "Мои файлы", id: "my-files", icon: FileText },
  { to: "/my-rewards", label: "Мои награды", id: "my-rewards", icon: Award },
  { to: "/plans", label: "Тарифы и чеки", id: "plans", icon: Crown },
  { to: "/support", label: "Поддержка", id: "support", icon: LifeBuoy },
  ...(isStaff(user) ? [{ to: "/admin", label: "Админ-панель", id: "admin", icon: ShieldCheck }] : []),
];

export function Logo() {
  return (
    <div className="flex items-center gap-2.5 shrink-0 whitespace-nowrap" data-testid="app-logo">
      <div className="h-9 w-9 rounded-lg bg-[#1f5f42] text-[#c8f2d6] grid place-items-center font-display font-extrabold text-sm">NH</div>
      <span className="font-display font-extrabold text-xl tracking-tight text-[#0b2e20] hidden min-[380px]:inline">Nis Hub</span>
    </div>
  );
}

function NavItems() {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <>
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.id}`}
          className={({ isActive }) => `whitespace-nowrap px-3 xl:px-3.5 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${isActive ? "bg-[#1f5f42] text-white" : "text-[#1f5f42] hover:bg-[#e8f5ed]"}`}>
          {n.label}
        </NavLink>
      ))}
      <DropdownMenu>
        <DropdownMenuTrigger className="px-3 py-2 rounded-full text-sm font-semibold text-[#1f5f42] hover:bg-[#e8f5ed] inline-flex items-center gap-1" data-testid="nav-more">Ещё<ChevronDown className="h-4 w-4" /></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {moreLinks(user).filter((l) => l.id !== "my-files").map((l) => (
            <DropdownMenuItem key={l.to} onClick={() => navigate(l.to)} className="gap-2 py-2.5 cursor-pointer" data-testid={`nav-${l.id}`}>
              <l.icon className="h-4 w-4 text-[#2e7d56]" />{l.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

const TABS = [
  { to: "/", label: "Предметы", id: "subjects", icon: BookOpen },
  { to: "/chat", label: "Чат", id: "chat", icon: MessageCircle },
  { to: "/ai", label: "AI", id: "ai", icon: Bot },
  { to: "/rewards", label: "Награды", id: "rewards", icon: Gift },
];

function MobileTabBar({ onMore }) {
  const tab = ({ isActive }) => `flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold transition-colors ${isActive ? "text-[#1f5f42]" : "text-[#7c9a8b]"}`;
  return (
    <nav className="xl:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-t border-[#d1e7dd] pb-[env(safe-area-inset-bottom)]" data-testid="mobile-tab-bar">
      <div className="grid grid-cols-5 h-16">
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.to === "/"} className={tab} data-testid={`mobile-nav-${t.id}`}>
            <t.icon className="h-5 w-5" />{t.label}
          </NavLink>
        ))}
        <button onClick={onMore} className="flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold text-[#7c9a8b]" data-testid="mobile-nav-more">
          <Menu className="h-5 w-5" />Ещё
        </button>
      </div>
    </nav>
  );
}

function MoreSheet({ open, onOpenChange }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const links = moreLinks(user);
  const go = (to) => { onOpenChange(false); navigate(to); };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl pb-[calc(1.5rem+env(safe-area-inset-bottom))]" data-testid="mobile-more-sheet">
        <SheetTitle className="sr-only">Меню</SheetTitle>
        <div className="flex items-center gap-3 pb-4 border-b border-[#d1e7dd]">
          <div className="h-11 w-11 rounded-full bg-[#1f5f42] text-white grid place-items-center font-bold">{user.username[0].toUpperCase()}</div>
          <div className="min-w-0">
            <div className="font-bold text-[#0b2e20] truncate">{user.username}</div>
            <div className="text-xs text-[#4b6b5c]">{ROLE_NAMES[user.role]} · {PLAN_NAMES[user.plan]} · {coinLabel(user)} коинов</div>
          </div>
        </div>
        <div className="py-2">
          {links.map((l) => (
            <button key={l.to} onClick={() => go(l.to)} className="w-full flex items-center gap-3 py-3.5 text-left font-semibold text-[#0b2e20] active:bg-[#e8f5ed] rounded-xl px-2" data-testid={`more-nav-${l.id}`}>
              <l.icon className="h-5 w-5 text-[#2e7d56]" />{l.label}
            </button>
          ))}
          <button onClick={async () => { onOpenChange(false); await logout(); navigate("/login"); }} className="w-full flex items-center gap-3 py-3.5 text-left font-semibold text-red-600 rounded-xl px-2" data-testid="more-logout-button">
            <LogOut className="h-5 w-5" />Выйти
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function UserBox() {
  const { user, logout, setGrade, openUpgrade } = useAuth();
  const navigate = useNavigate();
  const changeGrade = async (e) => {
    try { await setGrade(Number(e.target.value)); toast.success(`Класс изменён на ${e.target.value}`); }
    catch (err) { toast.error(errText(err)); }
  };
  return (
    <div className="flex items-center gap-2 sm:gap-3">
      <select value={user.grade || ""} onChange={changeGrade} data-testid="header-grade-select"
        className="h-9 rounded-full border border-[#d1e7dd] bg-white px-2 sm:px-3 text-sm font-semibold text-[#1f5f42] focus:outline-none focus:ring-2 focus:ring-[#2e7d56]">
        {GRADES.map((g) => <option key={g} value={g}>{g} класс</option>)}
      </select>
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/30 font-bold text-sm" data-testid="header-coin-balance">
        <Coins className="h-4 w-4" />{coinLabel(user)}
      </span>
      {user.plan === "free" ? (
        <button onClick={() => openUpgrade()} className="hidden sm:inline-flex items-center gap-1 h-9 px-3 rounded-full bg-[#0b2e20] text-amber-300 text-sm font-bold hover:bg-[#174731]" data-testid="header-upgrade-button"><Crown className="h-4 w-4" />Pro</button>
      ) : (
        <span className="hidden sm:inline-flex items-center gap-1 h-9 px-3 rounded-full bg-[#e8f5ed] text-[#1f5f42] text-sm font-bold" data-testid="header-plan-badge"><Crown className="h-4 w-4 text-amber-500" />{PLAN_NAMES[user.plan]}</span>
      )}
      <div className="hidden 2xl:block text-right leading-tight max-w-[160px]">
        <div className="text-sm font-bold text-[#0b2e20] truncate" data-testid="header-username">{user.username}</div>
        <div className="text-xs text-[#4b6b5c]">{ROLE_NAMES[user.role]}</div>
      </div>
      <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-button"
        className="hidden xl:grid h-9 w-9 place-items-center rounded-full text-[#1f5f42] hover:bg-[#e8f5ed] transition-colors" title="Выйти">
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function Layout() {
  const [more, setMore] = useState(false);
  const { user, openUpgrade, settings } = useAuth();
  useEffect(() => {
    const key = `nh-upsell-${user.id}`;
    if (settings && user.plan === "free" && !sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      openUpgrade("Откройте скачивание файлов и AI-помощника. Доступ на 30 дней.");
    }
  }, [user.id, user.plan, settings]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="min-h-screen bg-[#f6faf8]">
      <header className="backdrop-blur-md bg-white/85 border-b border-[#d1e7dd] sticky top-0 z-50 pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 xl:h-16 flex items-center justify-between gap-3">
          <NavLink to="/" data-testid="logo-home-link"><Logo /></NavLink>
          <nav className="hidden xl:flex items-center gap-0.5"><NavItems /></nav>
          <UserBox />
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-28 sm:pt-10 xl:py-12">
        <Outlet />
      </main>
      <MobileTabBar onMore={() => setMore(true)} />
      <MoreSheet open={more} onOpenChange={setMore} />
      <UpgradeDialog />
    </div>
  );
}
